// app/(main)/[slug]/_components/slug-main.tsx
"use client"

import { Container } from "@/components/common/container"
import { ProductCard } from "@/components/common/product-card"
import { FilterSection, type FilterState } from "@/components/filters/filter-section"
import {
  filtersToUrlParams,
  sortingOptions,
  urlParamsToFilters,
} from "@/components/filters/helpers"
import { MobileFilterDrawer } from "@/components/filters/mobile-filter-drawer"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useRouter, useSearchParams } from "next/navigation"
import { ArrowUpDown, Check } from "lucide-react"
import { useDeferredValue, useEffect, useState, useTransition } from "react"
import { FAQ } from "../../products/[id]/_components/faq"
import type { MetadataRecord } from "../page"
import { Breadcrumb } from "../../_components/Breadcrum"
import { BackButton } from "@/components/back-button"
import { AllProductsIcons } from "../../all-products/icons"
import { FiltersResponse, ProductsResponse } from "../../all-products/actions"
import SkeletonLoader from "../../all-products/_components/all-products-skeleton"

interface SlugMainProps {
  initialData: ProductsResponse
  filtersData: FiltersResponse
  initialFilters: FilterState
  initialSortBy: string
  searchQuery: string
  title: string
  metadata: MetadataRecord
}

export function SlugMain({
  initialData,
  filtersData,
  initialFilters,
  initialSortBy,
  searchQuery: initialSearchQuery,
  title,
  metadata,
}: SlugMainProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()

  const [sortDrawer, setSortDrawer] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [activeFilters, setActiveFilters] = useState<FilterState>(initialFilters)
  const [sortBy, setSortBy] = useState(initialSortBy)

  const searchQuery = searchParams?.get("q") ?? initialSearchQuery
  const debouncedSearchQuery = useDeferredValue(searchQuery)

  // Update URL and trigger server refetch when filters/sort change
  useEffect(() => {
    const params = filtersToUrlParams(activeFilters, sortBy)
    if (searchQuery) {
      params.set("q", searchQuery)
    }

    const newUrl = `?${params.toString()}`

    startTransition(() => {
      router.replace(newUrl)
    })
  }, [activeFilters, sortBy, searchQuery, router])

  // Sync state from URL changes (browser back/forward)
  useEffect(() => {
    const { filters: updatedFilters, sortBy: updatedSortBy } =
      urlParamsToFilters(searchParams)

    let hasChanges = false

    if (JSON.stringify(updatedFilters) !== JSON.stringify(activeFilters)) {
      setActiveFilters(updatedFilters)
      hasChanges = true
    }

    if (updatedSortBy !== sortBy) {
      setSortBy(updatedSortBy)
      hasChanges = true
    }

    if (hasChanges) {
      setIsUpdating(true)
      startTransition(() => {
        router.refresh()
      })
    }
  }, [searchParams])

  // Handle search query changes
  useEffect(() => {
    if (debouncedSearchQuery !== initialSearchQuery) {
      setIsUpdating(true)
      startTransition(() => {
        router.refresh()
      })
    }
  }, [debouncedSearchQuery, initialSearchQuery, router])

  useEffect(() => {
    if (!isPending) {
      setIsUpdating(false)
    }
  }, [isPending])

  const handleFilterChange = (filters: FilterState) => {
    setActiveFilters(filters)
  }

  const handleSortChange = (value: string) => {
    setSortBy(value)
  }

  const products = initialData?.products || []
  const filteredProducts =
      sortBy === "high" || sortBy === "low" || sortBy === "recent"
        ? products
        : products
        ? [...products].sort((a: any, b: any) => {
            const Aprice = a.payable / 100
            const Bprice = b.payable / 100
  
            // First, sort qty 0 products to the end
            if (a.qty === 0 && b.qty !== 0) return 1
            if (b.qty === 0 && a.qty !== 0) return -1
  
            // Then sort by price (descending)
            if (Bprice !== Aprice) {
              return Aprice - Bprice
            }
  
            // If price is equal, sort by qty (descending)
            return b.qty - a.qty
          })
        : []
  const totalCount = initialData?.totalCount || 0
  const isLoading = isPending || isUpdating

  return (
    <>
      <section className="flex flex-col items-center justify-center w-full my-8">
        <div className="flex w-full items-center mb-6">
          <div className="items-center gap-3 sm:w-[30%] sm:flex hidden">
            <BackButton className="text-xs" />
            <Breadcrumb className="font-gilroyRegular text-sm w-full sm:block hidden" />
          </div>

          <div className="w-full sm:w-[60%] flex justify-center items-center">
            <h1 className="text-center font-orange text-xl lg:text-3xl xl:text-4xl">
              {title}
            </h1>
          </div>
        </div>
        <div className="w-full flex gap-8">
          <FilterSection
            data={filtersData}
            initialFilters={activeFilters}
            onFilterChange={handleFilterChange}
            className="sm:w-[30%] sm:block hidden"
          />
          
          {/* RIGHT SECTION */}
          <div className="w-full flex flex-col gap-3 relative max-sm:w-full">
            <div className="flex justify-between items-center max-sm:hidden">
              <span className="text-[#828282] text-base sm:flex font-gilroySemiBold">
                {totalCount} Results found
              </span>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="font-gilroyMedium"
                  >
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    Sort By
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-[180px] font-gilroyMedium"
                >
                  <DropdownMenuRadioGroup
                    value={sortBy}
                    onValueChange={handleSortChange}
                  >
                    {sortingOptions?.map((opt) => (
                      <DropdownMenuRadioItem
                        key={opt?.value}
                        value={opt?.value}
                      >
                        {opt?.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* For Mobile View */}
            <div className="flex bg-[#F7F7F7] rounded-[7px] items-center justify-between px-4 sm:hidden">
              <MobileFilterDrawer
                data={filtersData}
                onFilterChange={handleFilterChange}
                initialFilters={initialFilters}
              >
                <button className="flex gap-2 items-center rounded-[6px] px-4 py-2">
                  <AllProductsIcons.filters_icon />
                  <span className="text-black font-gilroySemiBold text-sm">
                    Filters
                  </span>
                  <AllProductsIcons.arrowDown />
                </button>
              </MobileFilterDrawer>

              <div className="text-[#E1E1E1]">|</div>

              <button
                className="flex gap-2 items-center rounded-[6px] px-4 py-2"
                onClick={() => setSortDrawer(!sortDrawer)}
              >
                <img src="/sort.svg" alt="sortIcon" />
                <span className="text-black font-gilroySemiBold text-sm">
                  Sort By
                </span>
                {sortDrawer ? (
                  <AllProductsIcons.arrowUp />
                ) : (
                  <AllProductsIcons.arrowDown />
                )}
              </button>
            </div>

            {sortDrawer && (
              <Drawer
                open={sortDrawer}
                onClose={() => {
                  setSortDrawer(false)
                }}
              >
                <DrawerHeader className="sr-only">
                  <DrawerTitle>Sorting </DrawerTitle>
                </DrawerHeader>
                <DrawerContent>
                  <div className="p-2">
                    {sortingOptions.map((option) => (
                      <p
                        key={option?.value}
                        className="px-4 py-2 cursor-pointer flex justify-between items-center hover:bg-[#F7F7F7] hover:rounded-[6px] text-sm font-gilroySemiBold"
                        onClick={() => {
                          handleSortChange(option?.value)
                          setSortDrawer(false)
                        }}
                      >
                        <span>{option?.label}</span>
                        {sortBy === option?.value && (
                          <Check className="size-4" />
                        )}
                      </p>
                    ))}
                  </div>
                </DrawerContent>
              </Drawer>
            )}

            {isLoading ? (
              <SkeletonLoader />
            ) : (
              <div className="flex flex-wrap xs:gap-3 gap-2 lg:gap-y-8 px-2 sm:px-0 justify-between xs:justify-evenly sm:justify-start items-center w-full h-full mb-2 lg:mb-8">
                {products.length === 0 ? (
                  <div className="flex flex-col justify-center items-center w-full gap-12 max-sm:mt-16">
                    <img
                      src="/no-products.svg"
                      className="sm:size-40 size-32"
                      alt="no products found"
                    />
                    <div className="font-gilroy h-12 w-full text-center font-semibold leading-[19px] tracking-[0px]">
                      <p className="text-center font-gilroySemiBold sm:text-xl leading-[19px]">
                        No Products Available
                      </p>
                    </div>
                  </div>
                ) : (
                  filteredProducts.map((product, i: number) => (
                    <ProductCard
                      product={product}
                      res={3}
                      key={`${i}-${product.device_name}`}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <div className="mx-4 sm:mx-0 md:mx-10 xl:mx-40 lg:mt-5">
        <FAQ
          className="pt-8"
          faqs={metadata?.faq?.map(({ question, answer }) => ({
            question,
            answer,
          }))}
          title="Frequently Asked Questions"
        />

        <FAQ
          className="pt-12"
          title="Additional Information"
          faqs={[
            {
              question: metadata?.whyEdify?.title,
              answer: (
                <div className="flex gap-2 flex-col mb-4">
                  {metadata?.whyEdify?.content?.map((point, index) => (
                    <p key={index}>{point}</p>
                  ))}
                </div>
              ),
            },
            metadata?.features && {
              question: "Features",
              answer: (
                <div className="flex gap-2 flex-col mb-4">
                  {metadata.features.map((point, index) => (
                    <p key={index}>{point}</p>
                  ))}
                </div>
              ),
            },
            metadata?.useCases && {
              question: metadata.useCases.title,
              answer: (
                <div className="flex gap-2 flex-col mb-4">
                  {metadata.useCases.content.map((point, index) => (
                    <p key={index}>{point}</p>
                  ))}
                </div>
              ),
            },
            metadata?.pricing && {
              question: metadata.pricing.title,
              answer: (
                <div className="flex gap-2 flex-col mb-4">
                  {metadata.pricing.content.map((point, index) => (
                    <p key={index}>{point}</p>
                  ))}
                </div>
              ),
            },
          ].filter(Boolean)}
        />
      </div>
    </>
  )
}